import { AltoIcon, Button, Toast, ToastContext } from '@alto/design-system';
import * as React from 'react';
import { push } from 'react-router-redux';
// eslint-disable-next-line import/no-deprecated
import { requestVerification } from '~shared/actions/verifications';
import { COMMS_CHANNEL } from '~shared/constants';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type APIError } from '~shared/types';
import { CommsVerificationCard } from './CommsVerificationCard';

type Response = {
  success: boolean;
  error?: APIError;
};

export type Props = {
  readonly authorized: boolean;
  readonly commsChannel: string;
  readonly token: string;
};

export const CommsVerificationFail = ({ authorized, commsChannel, token }: Props) => {
  const dispatch = useDispatchShared();
  const requestVerificationLoading = useSelectorShared((state) => state.ui.loading.requestVerificationLoading);
  const { addToast } = React.useContext(ToastContext);
  const [isVerificationSent, setIsVerificationSent] = React.useState(false);
  const formattedChannelText = COMMS_CHANNEL[commsChannel as keyof typeof COMMS_CHANNEL].toLowerCase();

  const navigateToNextRoute = () => {
    if (authorized) {
      dispatch(
        push({
          pathname: '/prescriptions',
        }),
      );
    } else {
      // TODO: Get app store route
      dispatch(
        push({
          pathname: '/',
        }),
      );
    }
  };

  const handleSendingVerification = async () => {
    // eslint-disable-next-line import/no-deprecated
    const response: Response = await dispatch(requestVerification(token, commsChannel));
    if (response.success) {
      addToast(<Toast>{`Verification link has been sent to your ${formattedChannelText}!`}</Toast>);
      setIsVerificationSent(true);
    } else {
      addToast(<Toast variant="error">{`Error: ${getErrorMessageFromResponse(response)}`}</Toast>);
      setIsVerificationSent(false);
    }
  };

  return (
    <CommsVerificationCard
      title="Verification failed"
      text={`Your link has expired or an error occurred while attempting to verify your ${formattedChannelText}.`}
    >
      {isVerificationSent ? (
        <AltoIcon
          name="checkcircle-small"
          type="secondary"
        />
      ) : (
        <Button
          loading={requestVerificationLoading}
          loadingLabel="Sending..."
          label="Send new verification link"
          accessibilityLabel="Send new verification link"
          onPress={handleSendingVerification}
        />
      )}
      <Button
        label="Continue to Alto"
        accessibilityLabel="Continue to Alto"
        onPress={navigateToNextRoute}
        type="secondary"
      />
    </CommsVerificationCard>
  );
};
